// client/src/pages/Game/Battlefield/utils/targetFeedback.js
//
// R34ll : évaluation PURE du feedback du réticule de ciblage.
// (Séparée du DOM pour être testable unitairement.)
//
// Entrée : la carte dragguée, la cible détectée sous le doigt
// (format findDropTarget) et un résumé du gameState.
// Sortie : 'valid' | 'invalid' | 'neutral'.

export const isSpellCard = (card) =>
  !!(card && (card.type === 'SPELL' || card.cardType === 'SPELL'));

export const getSpellTargetNeeds = (card) => {
  const targets = new Set(
    (card?.effects || []).map((e) => e && e.target).filter(Boolean)
  );
  const needsEnemy = targets.has('ENEMY_SINGLE');
  const needsAlly = targets.has('ALLY_SINGLE');
  return { targets, needsEnemy, needsAlly, needsSpecific: needsEnemy || needsAlly };
};

const inDefenseMode = (gs) =>
  !!gs && gs.activePlayer === 'player' && !gs.playerHasAttack &&
  !!gs.opponentHasAttack &&
  !!gs.pendingAttack?.attackers?.length;

/**
 * @param {object} card   carte dragguée (location/zone/type/…)
 * @param {object|null} target  sortie de findDropTarget (kind, side, targetInstanceId…)
 * @param {object} gs     { activePlayer, playerHasAttack, opponentHasAttack,
 *                          pendingAttack, playerField, playerEnergy, playerSpellMana }
 * @returns {'valid'|'invalid'|'neutral'}
 */
export function evaluateDropFeedback(card, target, gs) {
  if (!card) return 'neutral';
  if (!target) return 'neutral'; // hors zones de jeu → neutre (pas rouge)

  const defense = inDefenseMode(gs);

  // ── Carte en main ──
  if (card.location === 'hand') {
    if (isSpellCard(card)) {
      const needs = getSpellTargetNeeds(card);
      if (target.kind === 'spell-target') {
        if (!needs.needsSpecific) return 'valid'; // cible acceptée quand même
        if (needs.needsEnemy && needs.needsAlly) return 'valid';
        if (needs.needsEnemy && !needs.needsAlly) return target.side === 'enemy' ? 'valid' : 'invalid';
        return target.side === 'ally' ? 'valid' : 'invalid';
      }
      if (target.kind === 'spell-cast') {
        // Sur le plateau sans carte visée : vert si le sort n'exige pas de cible
        return needs.needsSpecific ? 'invalid' : 'valid';
      }
      return 'neutral';
    }
    // Monstre en main
    if (target.kind === 'monster') return 'valid';         // → zone de monstres
    if (target.kind === 'battle') return 'invalid';        // zone de combat ≠ invocation
    if (target.kind === 'spell-target') return 'invalid';  // une carte monstre n'a pas d'effet ciblé drag
    return 'neutral';
  }

  // ── Monstre sur le terrain (drag attaque/défense, fantôme classique) ──
  if (card.location === 'field' && card.zone === 'monster') {
    if (defense) {
      if (target.kind === 'defense-slot') return 'valid';
      if (target.kind === 'battle') {
        // Zone de bataille générique : acceptée si des attaquants restent à bloquer
        const defended = new Set(
          (gs.playerField || [])
            .filter((c) => c.zone === 'battle' && c.role === 'defender')
            .map((c) => c.defendingAgainst)
        );
        const open = (gs.pendingAttack.attackers || []).some(
          (a) => !defended.has(a.position)
        );
        return open ? 'valid' : 'invalid';
      }
      return 'neutral';
    }
    if (target.kind === 'battle') return 'valid'; // déclaration d'attaque
    return 'neutral';
  }

  // ── Carte déjà engagée en zone de combat ──
  if (card.location === 'field' && card.zone === 'battle') {
    if (card.role === 'attacker') {
      if (target.kind === 'battle' && target.targetInfo === 'opponent') return 'valid';
      if (target.kind === 'monster') return 'valid'; // retrait vers zone monstres
      return 'neutral';
    }
    if (card.role === 'defender') {
      if (target.kind === 'monster') return 'valid'; // annulation de la défense
      return 'neutral';
    }
  }

  return 'neutral';
}
